import { defineMessages } from 'react-intl'

import { NP_SESSION_TYPES } from './constants'
import { CANCELATION_OPTIONS } from '../appointments/constants'
import { getDateFromAppointment } from '../appointments/getDateFromAppointment'

const sessionOrder: string[] = Object.values(NP_SESSION_TYPES)

type NpCancellationAppointment = {
  appointmentId: number
  sessionType?: string | null
  episodeId: string | null
  userId: string
  appointmentStatus: string
  startDate: string
  startTime: string
  timeZone: string
}

export const isNpAppointment = (appointment?: { sessionType?: string | null } | null): boolean =>
  sessionOrder.includes(appointment?.sessionType ?? '')

/** Only appointments in the same patient episode can be affected by the server's cascade. */
export function getNpCancellationAppointments<T extends NpCancellationAppointment>(target: T, appointments: T[]): T[] {
  if (!isNpAppointment(target)) return []
  if (!target.episodeId) return [target]
  const targetOrder = sessionOrder.indexOf(target.sessionType!)
  const dependents = appointments
    .filter(
      (appointment) =>
        appointment.appointmentId !== target.appointmentId &&
        appointment.userId === target.userId &&
        appointment.episodeId === target.episodeId &&
        sessionOrder.indexOf(appointment.sessionType ?? '') > targetOrder &&
        ['newAppt', 'tentative', 'rescheduled'].includes(appointment.appointmentStatus) &&
        getDateFromAppointment(appointment).getTime() > Date.now(),
    )
    .sort((a, b) => sessionOrder.indexOf(a.sessionType!) - sessionOrder.indexOf(b.sessionType!))
  return [target, ...dependents]
}

export const NP_SESSION_LABELS = defineMessages({
  [NP_SESSION_TYPES.NP_CLINICAL_INTAKE]: {
    defaultMessage: 'Clinical intake',
    description: 'NP clinical intake session label',
  },
  [NP_SESSION_TYPES.NP_TESTING_SESSION_1]: {
    defaultMessage: 'Testing session 1',
    description: 'NP first testing session label',
  },
  [NP_SESSION_TYPES.NP_TESTING_SESSION_2]: {
    defaultMessage: 'Testing session 2',
    description: 'NP second testing session label',
  },
  [NP_SESSION_TYPES.NP_POST_SESSION]: { defaultMessage: 'Feedback session', description: 'NP feedback session label' },
})

export const NP_CANCELLATION_REASONS = defineMessages({
  [CANCELATION_OPTIONS.needMoreTime]: {
    defaultMessage: 'Need more time to prepare',
    description: 'NP cancellation reason',
  },
  [CANCELATION_OPTIONS.lookingOtherCareOptions]: {
    defaultMessage: 'Looking for other care options',
    description: 'NP cancellation reason',
  },
  [CANCELATION_OPTIONS.readyToGraduate]: {
    defaultMessage: 'Ready to graduate from care',
    description: 'NP cancellation reason',
  },
  [CANCELATION_OPTIONS.emergency]: {
    defaultMessage: 'Emergency (personal/medical)',
    description: 'NP cancellation reason',
  },
  [CANCELATION_OPTIONS.notFeelingWell]: { defaultMessage: 'Not feeling well', description: 'NP cancellation reason' },
  [CANCELATION_OPTIONS.schedulingConflict]: {
    defaultMessage: 'Schedule conflict',
    description: 'NP cancellation reason',
  },
  [CANCELATION_OPTIONS.other]: { defaultMessage: 'Other', description: 'NP cancellation reason' },
})
